import PropTypes from 'prop-types';
import { ThemeProvider, useTheme, css } from '@emotion/react';
import SearchBox from '@veeva/corgix/searchbox';
import { Option } from '@veeva/corgix/select';
import ReactRouterLink from '../../components/ReactRouterLink';
import SelectField from '../../components/SelectField';
import getTargetAttribute from '../../services/getTargetAttribute';
import useMessageService from '../../services/i18n/useMessageService';
import { AuthenticatedPaths } from '../../constants/Paths';
import StudiesDataSortOptions from '../../constants/StudiesDataSortOptions';

const theme = {
    selectFieldInputWidth: '15rem',
};

const StudiesTitleDesktop = ({
    myStudies,
    searchTerm,
    handleSearch,
    searchPlaceholder,
    handleSortChange,
    sortOption,
    lastLoginTimeLabel,
    companyNameLabel,
    helpTextLabel,
    helpLinkLabel,
    loading,
}) => {
    const { grayLighter, silverDefault } = useTheme();
    const [[sortByLabel], messagesLoading] = useMessageService(['home.sort_by']);

    /**
     * In case the {0} in `helpTextLabel` is not at the end of the message,
     * Separate and render the halves before and after the {0} token
     */
    const indexOfToken = helpTextLabel.indexOf('{0}');
    const helpTextLabelWithoutToken = helpTextLabel.replace('{0}', '  ');
    const helpTextFirstHalf = helpTextLabelWithoutToken.slice(0, indexOfToken);
    const helpTextSecondHalf = helpTextLabelWithoutToken.slice(indexOfToken);

    const handleChange = (e, value) => {
        handleSearch(value);
    };

    const handleClear = () => {
        handleSearch('');
    };

    if (messagesLoading) {
        return null;
    }

    return (
        <ThemeProvider theme={theme}>
            <h1
                css={css`
                    font-size: 2rem;
                    font-weight: normal;
                    margin: 0;
                    padding-bottom: 2rem;
                `}
                data-target={getTargetAttribute('my-studies-title')}
            >
                {myStudies}
            </h1>
            <span
                css={css`
                    font-size: 1.1666rem;
                    color: ${grayLighter};
                `}
                data-target={getTargetAttribute('studies-help-message')}
            >
                {helpTextFirstHalf}
                <ReactRouterLink
                    css={css`
                        font-size: 1.1666rem;
                    `}
                    to={AuthenticatedPaths.SYSTEMS}
                    data-target={getTargetAttribute('studies-help-systems-link')}
                >
                    {helpLinkLabel}
                </ReactRouterLink>
                {helpTextSecondHalf}
            </span>
            <div
                css={css`
                    border-bottom: solid 2px ${silverDefault};
                    display: flex;
                    padding: 0.6667rem 0;
                    margin-bottom: 1rem;
                `}
            >
                <SearchBox
                    value={searchTerm}
                    onChange={handleChange}
                    onSearch={handleSearch}
                    onClear={handleClear}
                    placeholder={searchPlaceholder}
                    size="lg"
                    disabled={loading}
                />
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        padding-left: 1.6667rem;
                        gap: 0.3333rem;
                    `}
                >
                    <span
                        css={css`
                            font-weight: bold;
                        `}
                    >
                        {sortByLabel}
                    </span>
                    <SelectField
                        displayFieldCSS={css`
                            & > div > div:nth-of-type(2) {
                                display: none;
                            }
                        `}
                        selectCSS={css`
                            & > div {
                                height: 2.3333rem;
                                border-radius: 2px;
                            }
                        `}
                        menuFontSize="1rem"
                        disabled={loading}
                        onChange={handleSortChange}
                        value={sortOption}
                    >
                        <Option value={StudiesDataSortOptions.LASTLOGIN} label={lastLoginTimeLabel}>
                            {lastLoginTimeLabel}
                        </Option>
                        <Option value={StudiesDataSortOptions.COMPANYNAME} label={companyNameLabel}>
                            {companyNameLabel}
                        </Option>
                    </SelectField>
                </div>
            </div>
        </ThemeProvider>
    );
};

StudiesTitleDesktop.propTypes = {
    companyNameLabel: PropTypes.string,
    handleSearch: PropTypes.func,
    handleSortChange: PropTypes.func,
    helpLinkLabel: PropTypes.string,
    helpTextLabel: PropTypes.string,
    lastLoginTimeLabel: PropTypes.string,
    loading: PropTypes.bool,
    myStudies: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    searchTerm: PropTypes.string,
    sortOption: PropTypes.oneOf(Object.values(StudiesDataSortOptions)),
};

export default StudiesTitleDesktop;
